import { useEffect } from "react";

export default function LiquidGlassEffect({ targetSelector, width = 300, height = 200 }) {
  useEffect(() => {
    if (!targetSelector) return;

    const target = document.querySelector(targetSelector);
    if (!target) {
      console.warn(`LiquidGlassEffect: target "${targetSelector}" not found`);
      return;
    }

    // ========== Adaptasi Efek ==========
    function smoothStep(a, b, t) {
      t = Math.max(0, Math.min(1, (t - a) / (b - a)));
      return t * t * (3 - 2 * t);
    }
    function length(x, y) {
      return Math.sqrt(x * x + y * y);
    }
    function roundedRectSDF(x, y, width, height, radius) {
      const qx = Math.abs(x) - width + radius;
      const qy = Math.abs(y) - height + radius;
      return Math.min(Math.max(qx, qy), 0) + length(Math.max(qx, 0), Math.max(qy, 0)) - radius;
    }
    function texture(x, y) {
      return { type: "t", x, y };
    }
    function generateId() {
      return "liqglass-" + Math.random().toString(36).substr(2, 9);
    }

    class Shader {
      constructor(options = {}) {
        this.width = options.width;
        this.height = options.height;
        this.fragment = options.fragment;
        this.canvasDPI = 1;
        this.id = generateId();
        this.create();
        this.updateShader();
      }

      create() {
        // SVG filter
        this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.svg.setAttribute("width", "0");
        this.svg.setAttribute("height", "0");
        this.svg.style.position = "absolute";

        const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
        const filter = document.createElementNS("http://www.w3.org/2000/svg", "filter");
        filter.setAttribute("id", `${this.id}_filter`);
        filter.setAttribute("filterUnits", "objectBoundingBox");
        filter.setAttribute("colorInterpolationFilters", "sRGB");

        this.feImage = document.createElementNS("http://www.w3.org/2000/svg", "feImage");
        this.feImage.setAttribute("width", this.width);
        this.feImage.setAttribute("height", this.height);

        this.feDisplacementMap = document.createElementNS("http://www.w3.org/2000/svg", "feDisplacementMap");
        this.feDisplacementMap.setAttribute("in", "SourceGraphic");
        this.feDisplacementMap.setAttribute("in2", `${this.id}_map`);
        this.feDisplacementMap.setAttribute("xChannelSelector", "R");
        this.feDisplacementMap.setAttribute("yChannelSelector", "G");

        filter.appendChild(this.feImage);
        filter.appendChild(this.feDisplacementMap);
        defs.appendChild(filter);
        this.svg.appendChild(defs);

        // Canvas
        this.canvas = document.createElement("canvas");
        this.canvas.width = this.width * this.canvasDPI;
        this.canvas.height = this.height * this.canvasDPI;
        this.context = this.canvas.getContext("2d");

        target.style.backdropFilter = `url(#${this.id}_filter) blur(0.25px) contrast(1.1) brightness(1.1) saturate(1.1)`;
        target.style.webkitBackdropFilter = `url(#${this.id}_filter) blur(0.25px) contrast(1.1) brightness(1.1) saturate(1.1)`;
      }

      updateShader() {
        const w = this.width * this.canvasDPI;
        const h = this.height * this.canvasDPI;
        const data = new Uint8ClampedArray(w * h * 4);
        let maxScale = 0;
        const raw = [];

        for (let i = 0; i < data.length; i += 4) {
          const x = (i / 4) % w;
          const y = Math.floor(i / 4 / w);
          const pos = this.fragment({ x: x / w, y: y / h });
          const dx = pos.x * w - x;
          const dy = pos.y * h - y;
          maxScale = Math.max(maxScale, Math.abs(dx), Math.abs(dy));
          raw.push(dx, dy);
        }

        maxScale *= 0.5;
        let idx = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = raw[idx++] / maxScale + 0.5;
          const g = raw[idx++] / maxScale + 0.5;
          data[i] = r * 255;
          data[i + 1] = g * 255;
          data[i + 2] = 0;
          data[i + 3] = 255;
        }

        this.context.putImageData(new ImageData(data, w, h), 0, 0);
        this.feImage.setAttributeNS(
          "http://www.w3.org/1999/xlink",
          "href",
          this.canvas.toDataURL()
        );
        this.feDisplacementMap.setAttribute("scale", (maxScale / this.canvasDPI).toString());
      }

      appendTo(parent) {
        parent.appendChild(this.svg);
      }

      destroy() {
        this.svg.remove();
        this.canvas.remove();
      }
    }

    const shader = new Shader({
      width,
      height,
      fragment: (uv) => {
        const ix = uv.x - 0.5;
        const iy = uv.y - 0.5;
        const d = roundedRectSDF(ix, iy, 0.3, 0.2, 0.6);
        const disp = smoothStep(0.8, 0, d - 0.15);
        const scaled = smoothStep(0, 1, disp);
        return texture(ix * scaled + 0.5, iy * scaled + 0.5);
      },
    });

    shader.appendTo(target);
    window.liquidGlassLocal = shader;

    return () => shader.destroy();
  }, [targetSelector, width, height]);

  return null;
}
